/*
 * AUTO-GENERATED FILE — DO NOT EDIT.
 * Source of truth: packages/style-rules/src/
 * Regenerate with `pnpm generate:shared` (runs automatically at build/dev/install).
 */

/*!
 *
 *  Copyright (c) "Neo4j"
 *  Neo4j Sweden AB [http://neo4j.com]
 *
 *  This file is part of Neo4j.
 *
 *  Neo4j is free software: you can redistribute it and/or modify
 *  it under the terms of the GNU General Public License as published by
 *  the Free Software Foundation, either version 3 of the License, or
 *  (at your option) any later version.
 *
 *  This program is distributed in the hope that it will be useful,
 *  but WITHOUT ANY WARRANTY; without even the implied warranty of
 *  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 *  GNU General Public License for more details.
 *
 *  You should have received a copy of the GNU General Public License
 *  along with this program.  If not, see <http://www.gnu.org/licenses/>.
 */

import type { BigintLiteral, NumericLiteral } from './literal-schemas';

export type NumericValue = number | bigint;

type Rational = {
  denominator: bigint;
  numerator: bigint;
};

const BIGINT_ZERO = BigInt(0);
const BIGINT_ONE = BigInt(1);
const BIGINT_TWO = BigInt(2);
const BIGINT_RATIO_SCALE = BigInt('1000000000000000000');

function bigintPowerOfTen(exponent: number): bigint {
  return BigInt(`1${'0'.repeat(exponent)}`);
}

/** Resolves a native or JSON-safe numeric literal without reducing bigints. */
export function resolveNumericLiteral(value: NumericLiteral): NumericValue {
  return typeof value === 'object' ? BigInt(value.bigint) : value;
}

/** Returns whether a value is the JSON-safe bigint literal shape. */
export function isBigintLiteral(value: unknown): value is BigintLiteral {
  return (
    typeof value === 'object' &&
    value !== null &&
    'bigint' in value &&
    typeof value.bigint === 'string' &&
    /^[+-]?\d+$/.test(value.bigint)
  );
}

/**
 * Compares numeric values without coercing bigint operands to number.
 * Returns null for NaN, otherwise -1, 0, or 1.
 */
export function compareNumericValues(
  left: NumericValue,
  right: NumericValue,
): -1 | 0 | 1 | null {
  if (
    (typeof left === 'number' && Number.isNaN(left)) ||
    (typeof right === 'number' && Number.isNaN(right))
  ) {
    return null;
  }
  if (left < right) {
    return -1;
  }
  if (left > right) {
    return 1;
  }
  return 0;
}

/** Numeric equality that treats equivalent number and bigint values as equal. */
export function numericValuesEqual(
  left: NumericValue,
  right: NumericValue,
): boolean {
  return compareNumericValues(left, right) === 0;
}

/**
 * Clamps a numeric value between two endpoints. Inverted endpoints are
 * supported, and the original number/bigint representation is retained.
 */
export function clampNumericValue(
  value: NumericValue,
  firstBound: NumericValue,
  secondBound: NumericValue,
): NumericValue | null {
  const boundsOrder = compareNumericValues(firstBound, secondBound);
  if (boundsOrder === null) {
    return null;
  }
  const min = boundsOrder <= 0 ? firstBound : secondBound;
  const max = boundsOrder <= 0 ? secondBound : firstBound;
  const minOrder = compareNumericValues(value, min);
  const maxOrder = compareNumericValues(value, max);
  if (minOrder === null || maxOrder === null) {
    return null;
  }
  if (minOrder < 0) {
    return min;
  }
  if (maxOrder > 0) {
    return max;
  }
  return value;
}

function numberToRational(value: number): Rational | null {
  if (!Number.isFinite(value)) {
    return null;
  }

  const match = /^([+-]?)(\d+)(?:\.(\d+))?(?:e([+-]?\d+))?$/i.exec(
    value.toString(),
  );
  if (!match) {
    return null;
  }

  const [, sign, integer, fraction = '', exponentText = '0'] = match;
  const exponent = Number(exponentText) - fraction.length;
  const digits = BigInt(`${sign}${integer}${fraction}`);
  if (exponent >= 0) {
    return {
      denominator: BIGINT_ONE,
      numerator: digits * bigintPowerOfTen(exponent),
    };
  }
  return {
    denominator: bigintPowerOfTen(-exponent),
    numerator: digits,
  };
}

function toRational(value: NumericValue): Rational | null {
  return typeof value === 'bigint'
    ? { denominator: BIGINT_ONE, numerator: value }
    : numberToRational(value);
}

function divideBigints(numerator: bigint, denominator: bigint): number | null {
  if (denominator === BIGINT_ZERO) {
    return null;
  }

  const isNegative = numerator < BIGINT_ZERO !== denominator < BIGINT_ZERO;
  const absoluteNumerator = numerator < BIGINT_ZERO ? -numerator : numerator;
  const absoluteDenominator =
    denominator < BIGINT_ZERO ? -denominator : denominator;
  const integer = absoluteNumerator / absoluteDenominator;
  const remainder = absoluteNumerator % absoluteDenominator;
  const fraction =
    (remainder * BIGINT_RATIO_SCALE + absoluteDenominator / BIGINT_TWO) /
    absoluteDenominator;
  const result =
    Number(integer) + Number(fraction) / Number(BIGINT_RATIO_SCALE);

  return isNegative ? -result : result;
}

/**
 * Computes `(value - startValue) / (endValue - startValue)` while retaining
 * bigint precision. Only the final dimensionless interpolation weight is
 * converted to number.
 */
export function getNumericWeight(
  startValue: NumericValue,
  endValue: NumericValue,
  value: NumericValue,
): number | null {
  if (
    typeof startValue === 'number' &&
    typeof endValue === 'number' &&
    typeof value === 'number'
  ) {
    const weight = (value - startValue) / (endValue - startValue);
    return Number.isNaN(weight) ? null : weight;
  }

  const start = toRational(startValue);
  const end = toRational(endValue);
  const current = toRational(value);
  if (start === null || end === null || current === null) {
    return null;
  }

  const valueDelta =
    current.numerator * start.denominator -
    start.numerator * current.denominator;
  const rangeDelta =
    end.numerator * start.denominator - start.numerator * end.denominator;

  return divideBigints(
    valueDelta * end.denominator,
    current.denominator * rangeDelta,
  );
}
